/**
 * Shared vocabulary for authorization refusals, so the browser can tell one
 * apart from the other things a 403 means in this app: the trial-usage limit,
 * an expired embedded-form token (which means reload, not denied), a Metabase
 * seat check, or the user-seat limit on invite. Those all keep their own
 * handling; only a denial carrying {@link NOT_AUTHORIZED_ERROR} raises the
 * global "Not authorized" alert.
 *
 * Deliberately dependency-free: `requireAuth` (server-only) and the fetch
 * helpers (browser) both import it.
 */

export const NOT_AUTHORIZED_ERROR = "not_authorized";

/** Dispatched on `window` when a request is refused for lack of permission. */
export const NOT_AUTHORIZED_EVENT = "gail:not-authorized";

/**
 * Which gate refused, for logging. Not shown to the user — the alert copy is
 * deliberately generic, since naming the missing scope tells someone what to
 * go asking for.
 */
export type NotAuthorizedDetail = {
  dataType?: string;
  level?: string;
};

export type NotAuthorizedBody = NotAuthorizedDetail & {
  error: typeof NOT_AUTHORIZED_ERROR;
};

export function notAuthorizedBody(detail: NotAuthorizedDetail = {}): NotAuthorizedBody {
  return { error: NOT_AUTHORIZED_ERROR, ...detail };
}

/** The refusal detail if `responseText` is one of ours, otherwise `null`. */
export function parseNotAuthorized(responseText: string): NotAuthorizedDetail | null {
  if (!responseText || !responseText.includes(NOT_AUTHORIZED_ERROR)) return null;

  try {
    const parsed: unknown = JSON.parse(responseText);
    if (!parsed || typeof parsed !== "object") return null;

    const body = parsed as Record<string, unknown>;
    if (body.error !== NOT_AUTHORIZED_ERROR) return null;

    return {
      dataType: typeof body.dataType === "string" ? body.dataType : undefined,
      level: typeof body.level === "string" ? body.level : undefined,
    };
  } catch {
    return null;
  }
}

/**
 * One alert per burst. A page that fires several gated requests in parallel
 * gets one refusal message rather than a stack of identical toasts.
 */
const NOTIFY_COOLDOWN_MS = 3000;
let lastNotifiedAt = 0;

/** Test seam — the cooldown is module state that would leak between cases. */
export function resetNotAuthorizedCooldown(): void {
  lastNotifiedAt = 0;
}

/**
 * Announce a refusal so the mounted listener can alert. No-ops off the 403
 * path, on bodies that aren't ours, and on the server.
 */
export function notifyNotAuthorized(status: number, responseText: string): void {
  if (status !== 403) return;
  if (typeof window === "undefined") return;

  const detail = parseNotAuthorized(responseText);
  if (!detail) return;

  const now = Date.now();
  if (now - lastNotifiedAt < NOTIFY_COOLDOWN_MS) return;
  lastNotifiedAt = now;

  window.dispatchEvent(new CustomEvent<NotAuthorizedDetail>(NOT_AUTHORIZED_EVENT, { detail }));
}
